import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { 
  Box, 
  Paper, 
  Typography, 
  IconButton, 
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Slider,
  Switch,
  FormControlLabel
} from '@mui/material';
import { 
  ZoomIn, 
  ZoomOut, 
  CenterFocusStrong, 
  FilterList,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';

const KnowledgeGraph = ({ 
  graphData, 
  onNodeClick, 
  onNodeHover,
  selectedNodeId,
  width = "100%", 
  height = "100%" 
}) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const zoomRef = useRef(null);
  const simulationRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [filters, setFilters] = useState({
    showDocuments: true,
    showProtocols: true, 
    minSimilarity: 0.3,
    selectedDepartment: 'all',
    selectedRole: 'all'
  });

  // Handle responsive sizing
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width || 800,
          height: rect.height || 600
        });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    if (!graphData || !graphData.nodes || !graphData.edges || !svgRef.current) return;

    try {
      // Clear previous visualization
      d3.select(svgRef.current).selectAll("*").remove();

      const svg = d3.select(svgRef.current)
        .attr("width", dimensions.width)
        .attr("height", dimensions.height);

    // Create zoom behavior
    const zoomBehavior = d3.zoom()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        if (container && event && event.transform) {
          container.attr("transform", event.transform);
        }
      });

    try {
      svg.call(zoomBehavior);
      zoomRef.current = zoomBehavior;
    } catch (error) {
      console.error('Error initializing zoom behavior:', error);
    }

    // Create container for zoom/pan
    const container = svg.append("g");

    // Filter data based on current filters
    const filteredNodes = graphData.nodes.filter(node => {
      if (!filters.showDocuments && node.type === 'document') return false;
      if (!filters.showProtocols && node.type === 'protocol') return false;
      if (filters.selectedDepartment !== 'all' && node.department !== filters.selectedDepartment) return false;
      if (filters.selectedRole !== 'all' && !node.roles?.includes(filters.selectedRole)) return false;
      return true;
    });

    const filteredNodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredEdges = graphData.edges.filter(edge => 
      filteredNodeIds.has(edge.source) && 
      filteredNodeIds.has(edge.target) &&
      edge.weight >= filters.minSimilarity
    );

    // Create force simulation
    const sim = d3.forceSimulation(filteredNodes)
      .force("link", d3.forceLink(filteredEdges)
        .id(d => d.id)
        .distance(d => 100 - (d.weight * 50))
        .strength(d => d.weight)
      )
      .force("charge", d3.forceManyBody()
        .strength(d => d.type === 'protocol' ? -800 : -400)
      )
      .force("center", d3.forceCenter(dimensions.width / 2, dimensions.height / 2))
      .force("collision", d3.forceCollide()
        .radius(d => d.size + 5)
      );

    simulationRef.current = sim;

    // Create arrow markers for directed edges
    const defs = container.append("defs");
    
    defs.selectAll("marker")
      .data(["arrow"])
      .enter().append("marker")
      .attr("id", "arrow")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 15)
      .attr("refY", 0)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "#79D1C3");

    // Create edges with better visibility
    const links = container.append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(filteredEdges)
      .enter().append("line")
      .attr("stroke", d => d.color || "#79D1C3")
      .attr("stroke-width", d => Math.max(2, d.width))
      .attr("stroke-opacity", 0.8)
      .attr("marker-end", "url(#arrow)");

    // Create nodes with enhanced visuals
    const nodeGroup = container.append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(filteredNodes)
      .enter().append("g")
      .attr("class", "node")
      .style("cursor", "pointer")
      .call(d3.drag()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended)
      );

    // Add outer ring for protocols with glassmorphism
    nodeGroup.filter(d => d.type === 'protocol')
      .append("circle")
      .attr("r", d => d.size + 5)
      .attr("fill", "rgba(255, 255, 255, 0.1)")
      .attr("stroke", "rgba(255, 255, 255, 0.6)")
      .attr("stroke-width", 3)
      .style("filter", "blur(0.5px)");

    // Add main node circles with vibrant colors
    const nodes = nodeGroup.append("circle")
      .attr("r", d => d.size)
      .attr("fill", d => {
        // Use more vibrant, visible colors
        if (d.type === 'protocol') return "#B995C9"; // Purple from theme
        return "#79D1C3"; // Teal from theme
      })
      .attr("stroke", "rgba(255, 255, 255, 0.8)")
      .attr("stroke-width", d => d.type === 'protocol' ? 4 : 3)
      .style("filter", "drop-shadow(0px 4px 12px rgba(0,0,0,0.4))")
      .style("opacity", 0.95)
      .on("click", (event, d) => {
        event.stopPropagation();
        onNodeClick && onNodeClick(d);
      })
      .on("mouseenter", (event, d) => {
        // Highlight connected nodes
        const connectedNodeIds = new Set();
        filteredEdges.forEach(edge => {
          if (edge.source.id === d.id) connectedNodeIds.add(edge.target.id);
          if (edge.target.id === d.id) connectedNodeIds.add(edge.source.id);
        });

        nodes.style("opacity", node => 
          node.id === d.id || connectedNodeIds.has(node.id) ? 1 : 0.3
        );
        
        links.style("opacity", edge => 
          edge.source.id === d.id || edge.target.id === d.id ? 0.8 : 0.1
        );

        onNodeHover && onNodeHover(d);
      })
      .on("mouseleave", () => {
        nodes.style("opacity", 1);
        links.style("opacity", 0.6);
      });

    // Add icons to nodes
    nodeGroup.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .attr("font-size", d => Math.min(d.size * 0.6, 16))
      .attr("fill", "white")
      .attr("font-weight", "bold")
      .style("pointer-events", "none")
      .text(d => d.type === 'protocol' ? 'P' : 'D');

    // Add glassmorphism background for labels
    nodeGroup.append("rect")
      .attr("x", d => d.size + 5)
      .attr("y", -12)
      .attr("width", d => {
        const text = d.title.length > 20 ? d.title.substring(0, 20) + "..." : d.title;
        return text.length * 7 + 10;
      })
      .attr("height", 24)
      .attr("rx", 4)
      .attr("fill", "rgba(255, 255, 255, 0.15)")
      .attr("stroke", "rgba(255, 255, 255, 0.3)")
      .attr("stroke-width", 1)
      .style("backdrop-filter", "blur(10px)")
      .style("pointer-events", "none");

    // Add labels with better contrast
    const labels = nodeGroup.append("text")
      .attr("dx", d => d.size + 10)
      .attr("dy", "0.35em")
      .attr("font-size", "13px")
      .attr("fill", "#ffffff")
      .attr("font-weight", "600")
      .style("pointer-events", "none")
      .style("text-shadow", "0 2px 4px rgba(0,0,0,0.5)")
      .text(d => d.title.length > 20 ? d.title.substring(0, 20) + "..." : d.title);

    // Add department badges with glassmorphism
    const deptGroup = nodeGroup.filter(d => d.department);
    
    deptGroup.append("rect")
      .attr("x", d => d.size + 5)
      .attr("y", 14)
      .attr("width", d => (d.department?.length || 0) * 6 + 8)
      .attr("height", 16)
      .attr("rx", 3)
      .attr("fill", "rgba(121, 209, 195, 0.2)")
      .attr("stroke", "rgba(121, 209, 195, 0.5)")
      .attr("stroke-width", 1)
      .style("pointer-events", "none");

    deptGroup.append("text")
      .attr("dx", d => d.size + 9)
      .attr("dy", "25")
      .attr("font-size", "10px")
      .attr("fill", "#E0F2F1")
      .attr("font-weight", "500")
      .style("pointer-events", "none")
      .text(d => d.department);

    // Highlight selected node
    if (selectedNodeId) {
      nodes.filter(d => d.id === selectedNodeId)
        .attr("stroke", "#ff6b6b")
        .attr("stroke-width", 4);
    }

    // Update positions on simulation tick
    sim.on("tick", () => {
      links
        .attr("x1", d => d.source.x)
        .attr("y1", d => d.source.y)
        .attr("x2", d => d.target.x)
        .attr("y2", d => d.target.y);

      nodeGroup
        .attr("transform", d => `translate(${d.x},${d.y})`);
    });

    // Drag functions
    function dragstarted(event, d) {
      if (!event.active) sim.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event, d) {
      if (!event.active) sim.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    // Cleanup
    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop();
      }
    };

    } catch (error) {
      console.error('Error initializing knowledge graph:', error);
      return () => {}; // Return empty cleanup function on error
    }

  }, [graphData, filters, selectedNodeId, dimensions]);

  const handleZoomIn = () => {
    if (zoomRef.current && svgRef.current) {
      d3.select(svgRef.current).transition().call(
        zoomRef.current.scaleBy, 1.5
      );
    }
  };

  const handleZoomOut = () => {
    if (zoomRef.current && svgRef.current) {
      d3.select(svgRef.current).transition().call(
        zoomRef.current.scaleBy, 1 / 1.5  
      );
    }
  };

  const handleCenter = () => {
    if (zoomRef.current && svgRef.current) {
      d3.select(svgRef.current).transition().call(
        zoomRef.current.transform,
        d3.zoomIdentity
      );
    }
  };

  const departments = (graphData?.stats?.departments || []).filter(Boolean);
  const roles = (graphData?.stats?.roles || []).filter(Boolean);

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Controls Panel with Glassmorphism */}
      <Paper sx={{ 
        position: 'absolute', 
        top: 10, 
        left: 10, 
        p: 2, 
        zIndex: 1000,
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        minWidth: 250
      }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#fff' }}>
          Knowledge Graph Controls
        </Typography>

        {/* Node Type Toggles */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={filters.showDocuments}
                onChange={(e) => setFilters(prev => ({...prev, showDocuments: e.target.checked}))}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#79D1C3',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#79D1C3',
                  },
                }}
              />
            }
            label="Documents"
            sx={{ color: '#fff' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filters.showProtocols}
                onChange={(e) => setFilters(prev => ({...prev, showProtocols: e.target.checked}))}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#B995C9',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#B995C9',
                  },
                }}
              />
            }
            label="Protocols"
            sx={{ color: '#fff' }}
          />
        </Box>

        {/* Department Filter */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Department</InputLabel>
          <Select
            value={filters.selectedDepartment}
            onChange={(e) => setFilters(prev => ({...prev, selectedDepartment: e.target.value}))}
            sx={{
              color: '#fff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#79D1C3',
              },
              '& .MuiSvgIcon-root': {
                color: '#fff',
              },
            }}
          >
            <MenuItem value="all">All Departments</MenuItem>
            {departments.map(dept => (
              <MenuItem key={dept} value={dept}>{dept || 'Uncategorized'}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Role Filter */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Role</InputLabel>
          <Select
            value={filters.selectedRole}
            onChange={(e) => setFilters(prev => ({...prev, selectedRole: e.target.value}))}
            sx={{
              color: '#fff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#B995C9',
              },
              '& .MuiSvgIcon-root': {
                color: '#fff',
              },
            }}
          >
            <MenuItem value="all">All Roles</MenuItem>
            {roles.map(role => (
              <MenuItem key={role} value={role}>{role}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Similarity Threshold */}
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#fff', fontWeight: 600 }}>
          Min Similarity: {filters.minSimilarity}
        </Typography>
        <Slider
          value={filters.minSimilarity}
          onChange={(e, value) => setFilters(prev => ({...prev, minSimilarity: value}))}
          min={0.1}
          max={0.9}
          step={0.1}
          size="small"
          sx={{
            color: '#79D1C3',
            '& .MuiSlider-thumb': {
              backgroundColor: '#fff',
              border: '2px solid #79D1C3',
            },
            '& .MuiSlider-track': {
              backgroundColor: '#79D1C3',
            },
            '& .MuiSlider-rail': {
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        />
      </Paper>

      {/* Zoom Controls with Glassmorphism */}
      <Box sx={{ 
        position: 'absolute', 
        top: 10, 
        right: 10, 
        display: 'flex', 
        flexDirection: 'column',
        gap: 1,
        zIndex: 1000
      }}>
        <Tooltip title="Zoom In">
          <IconButton 
            onClick={handleZoomIn} 
            size="small" 
            sx={{ 
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: '#fff',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.25)',
              }
            }}
          >
            <ZoomIn />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out">
          <IconButton 
            onClick={handleZoomOut} 
            size="small" 
            sx={{ 
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: '#fff',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.25)',
              }
            }}
          >
            <ZoomOut />
          </IconButton>
        </Tooltip>
        <Tooltip title="Center View">
          <IconButton 
            onClick={handleCenter} 
            size="small" 
            sx={{ 
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: '#fff',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.25)',
              }
            }}
          >
            <CenterFocusStrong />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Graph Stats with Glassmorphism */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 10, 
        left: 10, 
        zIndex: 1000
      }}>
        <Paper sx={{ 
          p: 1.5, 
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}>
          <Typography variant="caption" sx={{ display: 'block', color: '#fff', fontWeight: 600 }}>
            {graphData?.stats?.total_nodes || 0} nodes, {graphData?.stats?.total_edges || 0} connections
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Chip 
              label={`Docs: ${graphData?.stats?.document_nodes || 0}`} 
              size="small"
              sx={{
                background: 'rgba(121, 209, 195, 0.3)',
                color: '#fff',
                fontWeight: 600,
                border: '1px solid rgba(121, 209, 195, 0.5)'
              }}
            />
            <Chip 
              label={`Protocols: ${graphData?.stats?.protocol_nodes || 0}`} 
              size="small"
              sx={{
                background: 'rgba(185, 149, 201, 0.3)',
                color: '#fff',
                fontWeight: 600,
                border: '1px solid rgba(185, 149, 201, 0.5)'
              }}
            />
          </Box>
        </Paper>
      </Box>

      {/* Main SVG with darker background */}
      <svg
        ref={svgRef}
        style={{
          width: '100%',
          height: '100%',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 8,
          background: 'linear-gradient(135deg, rgba(185, 149, 201, 0.2) 0%, rgba(121, 209, 195, 0.2) 45%, rgba(248, 195, 141, 0.2) 100%)'
        }}
      />
    </Box>
  );
};

export default KnowledgeGraph;